// IslandGenerator.js
// Rewritten with a penalty-based radial approach to avoid "bullseye" islands.

class IslandGenerator {
  /**
   * Generate an island mask using noise and radial gradients
   * @param {number} width - Width of the island mask
   * @param {number} height - Height of the island mask
   * @param {Object} options - Island generation options
   * @param {number} options.noiseScale - Scale of the noise (controls feature size)
   * @param {number} options.noiseThreshold - Threshold for noise values to be considered land (0-1)
   * @param {number} options.octaves - Number of octaves for the noise
   * @param {number} options.persistence - Persistence value for fractal noise
   * @param {number} options.lacunarity - Lacunarity multiplier for each octave
   * @param {number} options.falloffStrength - Exponent for distance-based penalty
   * @param {number} options.edgeWidth - Maximum penalty near the edges (0-1)
   * @param {string|number} options.seed - Seed for the noise generator
   * @returns {number[][]} 2D array of mask values between 0 and 1 (0 = water, 1 = land)
   */
  static generateIslandMask(width, height, options = {}) {
    // Default options
    const defaultOptions = {
      noiseScale: 100,
      noiseThreshold: 0.35, // used to decide between water and land in mask conversion
      octaves: 4,
      persistence: 0.5,
      lacunarity: 2.0,
      falloffStrength: 2.0,
      edgeWidth: 0.45,
      seed: 'island-default'
    };

    // Merge user options with defaults
    const config = { ...defaultOptions, ...options };

    // Generate a unified terrain height map using the improved penalty-based approach
    const heightMap = this.generateUnifiedTerrainMap(width, height, {
      noiseScale: config.noiseScale,
      octaves: config.octaves,
      persistence: config.persistence,
      lacunarity: config.lacunarity,
      falloffStrength: config.falloffStrength,
      edgeWidth: config.edgeWidth,
      heightScale: 1.0, // leave scaling for mask conversion
      waterThreshold: config.noiseThreshold,
      seed: config.seed
    });

    // Convert height map to island mask using the same thresholds as before
    const mask = Array(height)
      .fill(0)
      .map(() => Array(width).fill(0));

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const terrainType = this.getTerrainTypeFromHeight(heightMap[y][x]);

        if (terrainType === 'deep_water') {
          mask[y][x] = 0;
        } else if (terrainType === 'shallow_water') {
          mask[y][x] = 0.3;
        } else if (terrainType === 'beach') {
          mask[y][x] = 0.7;
        } else {
          mask[y][x] = 1;
        }
      }
    }

    return mask;
  }

  /**
   * Generate a preview of the island mask as a data URL
   * (Unchanged from original)
   */
  static generateIslandMaskPreview(mask, colorMap = 'island') {
    const width = mask[0].length;
    const height = mask.length;

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    const imageData = ctx.createImageData(width, height);

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const maskValue = mask[y][x];
        const index = (y * width + x) * 4;

        if (colorMap === 'grayscale') {
          const value = Math.floor(maskValue * 255);
          imageData.data[index] = value;
          imageData.data[index + 1] = value;
          imageData.data[index + 2] = value;
          imageData.data[index + 3] = 255;
        } else if (colorMap === 'island') {
          if (maskValue === 0) {
            // Deep water
            imageData.data[index] = 0;
            imageData.data[index + 1] = 50;
            imageData.data[index + 2] = 150;
          } else if (maskValue === 0.3) {
            // Shallow water
            imageData.data[index] = 50;
            imageData.data[index + 1] = 150;
            imageData.data[index + 2] = 200;
          } else if (maskValue === 0.7) {
            // Beach
            imageData.data[index] = 230;
            imageData.data[index + 1] = 210;
            imageData.data[index + 2] = 160;
          } else {
            // Land
            imageData.data[index] = 50;
            imageData.data[index + 1] = 180;
            imageData.data[index + 2] = 50;
          }
          imageData.data[index + 3] = 255;
        }
      }
    }

    ctx.putImageData(imageData, 0, 0);
    return canvas.toDataURL();
  }

  /**
   * Generate a height map based on an island mask.
   * (Keeps backwards compatibility by blending with the unified terrain map.)
   */
  static generateHeightMap(width, height, islandMask, options = {}) {
    const defaultOptions = {
      noiseScale: 370,
      octaves: 5,
      persistence: 0.5,
      lacunarity: 2.0,
      heightScale: 0.2,
      seed: 'height-default'
    };

    const config = { ...defaultOptions, ...options };

    // Generate an improved unified height map (penalty-based approach)
    const unifiedHeightMap = this.generateUnifiedTerrainMap(width, height, {
      noiseScale: config.noiseScale,
      octaves: config.octaves,
      persistence: config.persistence,
      lacunarity: config.lacunarity,
      falloffStrength: 2.0, // you can tweak these if needed
      edgeWidth: 0.3,
      heightScale: config.heightScale,
      seed: config.seed
    });

    const heightMap = Array(height)
      .fill(0)
      .map(() => Array(width).fill(0));

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const maskValue = islandMask[y][x];
        const newHeightValue = unifiedHeightMap[y][x];

        let heightValue = 0;

        if (maskValue === 0) {
          heightValue = 0.05 + newHeightValue * 0.05;
        } else if (maskValue === 0.3) {
          heightValue = 0.1 + newHeightValue * 0.1;
        } else if (maskValue === 0.7) {
          heightValue = 0.25 + newHeightValue * 0.15;
        } else {
          heightValue = 0.4 + newHeightValue * 0.6;
        }

        heightValue *= config.heightScale;
        heightValue = Math.max(0, Math.min(1, heightValue));

        heightMap[y][x] = heightValue;
      }
    }

    return heightMap;
  }

  /**
   * Generate a preview of the height map as a data URL.
   * (Unchanged from original)
   */
  static generateHeightMapPreview(heightMap, colorMap = 'terrain') {
    const width = heightMap[0].length;
    const height = heightMap.length;

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    const imageData = ctx.createImageData(width, height);

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const heightValue = heightMap[y][x];
        const index = (y * width + x) * 4;

        if (colorMap === 'grayscale') {
          const value = Math.floor(heightValue * 255);
          imageData.data[index] = value;
          imageData.data[index + 1] = value;
          imageData.data[index + 2] = value;
          imageData.data[index + 3] = 255;
        } else if (colorMap === 'terrain') {
          if (heightValue < 0.1) {
            imageData.data[index] = 0;
            imageData.data[index + 1] = 20;
            imageData.data[index + 2] = 120;
          } else if (heightValue < 0.2) {
            imageData.data[index] = 30;
            imageData.data[index + 1] = 120;
            imageData.data[index + 2] = 190;
          } else if (heightValue < 0.3) {
            imageData.data[index] = 240;
            imageData.data[index + 1] = 220;
            imageData.data[index + 2] = 150;
          } else if (heightValue < 0.5) {
            imageData.data[index] = 40;
            imageData.data[index + 1] = 180;
            imageData.data[index + 2] = 40;
          } else if (heightValue < 0.7) {
            const greenVal = 140 - (heightValue - 0.5) * 200;
            imageData.data[index] = 80;
            imageData.data[index + 1] = greenVal;
            imageData.data[index + 2] = 30;
          } else if (heightValue < 0.85) {
            const mix = (heightValue - 0.7) / 0.15;
            imageData.data[index] = 100 + mix * 50;
            imageData.data[index + 1] = 80 + mix * 50;
            imageData.data[index + 2] = 30 + mix * 70;
          } else {
            const whiteAmount = (heightValue - 0.85) / 0.15;
            const grayVal = 150 + whiteAmount * 105;
            imageData.data[index] = grayVal;
            imageData.data[index + 1] = grayVal;
            imageData.data[index + 2] = grayVal;
          }
          imageData.data[index + 3] = 255;
        }
      }
    }

    ctx.putImageData(imageData, 0, 0);
    return canvas.toDataURL();
  }

  /**
   * Generate a unified terrain map using a "penalty-based" approach
   * instead of a hard radial multiply. This avoids perfect bullseye rings.
   *
   * @param {number} width - Width of the terrain map
   * @param {number} height - Height of the terrain map
   * @param {Object} options - Terrain generation options
   * @param {number} options.noiseScale - Scale of the noise (affects base frequency)
   * @param {number} options.octaves - Number of octaves for fractal noise
   * @param {number} options.persistence - Persistence value (amplitude reduction per octave)
   * @param {number} options.lacunarity - Frequency multiplier for each octave
   * @param {number} options.falloffStrength - Exponent for distance penalty
   * @param {number} options.edgeWidth - Maximum penalty near edges
   * @param {number} options.heightScale - Multiplier for the final height values
   * @param {number} options.waterThreshold - (Unused here; kept for compatibility)
   * @param {string|number} options.seed - Seed for the noise generator
   * @param {number} options.landVariance - Additional variance for non-water regions (default: 0.15)
   * @param {number} options.landNoiseScale - Scale for additional noise in land areas (default: 600)
   * @returns {number[][]} 2D array of height values between 0 and 1
   */
  static generateUnifiedTerrainMap(width, height, options = {}) {
    const defaultOptions = {
      noiseScale: 100,
      octaves: 6,
      persistence: 0.5,
      lacunarity: 1.0,
      falloffStrength: 2.0, // controls how strongly distance penalizes height
      edgeWidth: 0.3,       // maximum penalty
      heightScale: 1.0,     // Reduced from 3.0 to ensure water areas stay below threshold
      waterThreshold: 0.35, // not used in this method, kept for consistency
      seed: 'unified-terrain',
      landVariance: 0.55,   // amount of additional variance for land areas
      landNoiseScale: 400,  // higher frequency for detail noise
      mountainScale: 1.8    // exponent to boost mountain height (new)
    };

    const config = { ...defaultOptions, ...options };

    // Create a seeded permutation table for Perlin noise
    const perm = this.seededPermutation(config.seed.toString());
    // Create a different seed for the detail noise
    const detailSeed = config.seed.toString() + '-detail';
    const detailPerm = this.seededPermutation(detailSeed);
    // Create a seed specifically for ridge generation
    const ridgeSeed = config.seed.toString() + '-ridge';
    const ridgePerm = this.seededPermutation(ridgeSeed);

    // Prepare the height map array
    const heightMap = Array(height)
      .fill(0)
      .map(() => Array(width).fill(0));

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        // Normalize coords to [-0.5..0.5]
        const nx = x / width - 0.5;
        const ny = y / height - 0.5;

        // Compute fractal Perlin noise in [0..1]
        let amplitude = 1;
        let frequency = config.noiseScale / 100;
        let noiseValue = 0;
        let maxAmplitude = 0;

        for (let o = 0; o < config.octaves; o++) {
          const val = this.noise2D(nx * frequency, ny * frequency, perm);
          noiseValue += val * amplitude;
          maxAmplitude += amplitude;
          amplitude *= config.persistence;
          frequency *= config.lacunarity;
        }
        // Normalize to [0..1]
        let normalizedNoise = (noiseValue / maxAmplitude + 1) * 0.5;
        // Without any radial factor, this alone can produce random terrain.

        // Distance from center in [0..1], but do not forcibly set to water at distance=1
        let distance = Math.sqrt(nx * nx + ny * ny) * 2;
        if (distance > 1) distance = 1; // clamp

        // Penalty-based approach: subtract some fraction of edgeWidth
        // The penalty grows with distance^falloffStrength
        let penalty = Math.pow(distance, config.falloffStrength) * config.edgeWidth;
        
        // Increase the penalty for water generation by adding a small constant
        penalty += 0.1;

        // Subtract penalty from noise. If it goes below 0 => water.
        let rawHeight = normalizedNoise - penalty;

        // Clamp negative to 0
        if (rawHeight < 0) rawHeight = 0;

        // Apply overall height scaling
        let finalHeight = rawHeight * config.heightScale;
        
        // Ensure water regions stay as water
        if (finalHeight < 0.2) {
            // Cap water height to ensure it's recognized as water
            finalHeight = Math.min(finalHeight, 0.19);
        }
        
        // Add additional variance to non-water areas (height > 0.2)
        if (finalHeight > 0.2) {
          // Generate higher frequency detail noise
          const detailFrequency = config.landNoiseScale / 100;
          const detailNoise = this.noise2D(nx * detailFrequency, ny * detailFrequency, detailPerm);
          
          // Scale the noise and add it to the height, with more effect at higher elevations
          const intensityFactor = (finalHeight - 0.2) / 0.8; // 0 at water boundary, 1 at max height
          const detailIntensity = intensityFactor * config.landVariance;
          
          // Apply the detail noise, scaled by both the landVariance and the intensity factor
          finalHeight += detailNoise * detailIntensity;
          
          // Add some micro-variations for more realistic terrain
          const microScale = config.landNoiseScale * 3;
          const microNoise = this.noise2D(nx * microScale, ny * microScale, detailPerm) * 0.05;
          finalHeight += microNoise * intensityFactor;
          
          // Add ridge formations to create more dramatic mountain features
          if (finalHeight > 0.5) {
            // Generate ridge noise with a different frequency
            const ridgeFreq = config.landNoiseScale / 70;
            const angle = Math.atan2(ny, nx) * 5; // Create directional bias
            const ridgeNoise = Math.abs(this.noise2D(nx * ridgeFreq + angle, ny * ridgeFreq, ridgePerm));
            
            // Ridge intensity increases with height
            const ridgeFactor = ((finalHeight - 0.5) / 0.5) * 0.25; 
            finalHeight += ridgeNoise * ridgeFactor;
            
            // Apply non-linear transformation to exaggerate mountains
            if (finalHeight > 0.65) {
              const mountainFactor = (finalHeight - 0.65) / 0.35;
              // Power function to boost mountains more dramatically
              finalHeight = 0.65 + (Math.pow(mountainFactor, config.mountainScale) * 0.35);
            }
          }
        }

        // Clamp final height
        if (finalHeight > 1) finalHeight = 1;
        if (finalHeight < 0) finalHeight = 0;

        heightMap[y][x] = finalHeight;
      }
    }

    return heightMap;
  }

  /**
   * Get terrain type based on height value.
   * (Unchanged from original)
   */
  static getTerrainTypeFromHeight(height) {
    if (height < 0.1) {
      return 'deep_water';
    } else if (height < 0.2) {
      return 'shallow_water';
    } else if (height < 0.3) {
      return 'beach';
    } else if (height < 0.5) {
      return 'lowland';
    } else if (height < 0.7) {
      return 'hills';
    } else if (height < 0.85) {
      return 'mountains';
    } else {
      return 'peaks';
    }
  }

  /**
   * Generate a complete terrain map with both height and type information.
   * (Unchanged from original)
   * @param {number} width
   * @param {number} height
   * @param {Object} options
   * @returns {Object} { heightMap, typeMap }
   */
  static generateCompleteTerrainMap(width, height, options = {}) {
    const heightMap = this.generateUnifiedTerrainMap(width, height, options);
    const typeMap = Array(height)
      .fill(0)
      .map(() => Array(width).fill(''));

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        typeMap[y][x] = this.getTerrainTypeFromHeight(heightMap[y][x]);
      }
    }

    return {
      heightMap,
      typeMap
    };
  }

  // --- Perlin Noise Helper Functions ---

  /**
   * Seeded permutation generator for Perlin noise.
   * Returns a Uint8Array of 512 values.
   * @param {string} seedStr - The seed string.
   */
  static seededPermutation(seedStr) {
    let seed = 0;
    for (let i = 0; i < seedStr.length; i++) {
      seed += seedStr.charCodeAt(i);
    }
    const p = new Array(256);
    for (let i = 0; i < 256; i++) {
      p[i] = i;
    }
    // Simple Fisher-Yates shuffle using a basic PRNG
    for (let i = 255; i > 0; i--) {
      seed = (seed * 9301 + 49297) % 233280;
      const j = seed % (i + 1);
      [p[i], p[j]] = [p[j], p[i]];
    }
    const perm = new Uint8Array(512);
    for (let i = 0; i < 512; i++) {
      perm[i] = p[i & 255];
    }
    return perm;
  }

  /**
   * Fade function for Perlin noise.
   * @param {number} t
   */
  static fade(t) {
    // 6t^5 - 15t^4 + 10t^3
    return t * t * t * (t * (t * 6 - 15) + 10);
  }

  /**
   * Linear interpolation.
   * @param {number} a
   * @param {number} b
   * @param {number} t
   */
  static lerp(a, b, t) {
    return a + t * (b - a);
  }

  /**
   * Gradient function for Perlin noise.
   * @param {number} hash
   * @param {number} x
   * @param {number} y
   */
  static grad(hash, x, y) {
    switch (hash & 3) {
      case 0:
        return x + y;
      case 1:
        return -x + y;
      case 2:
        return x - y;
      case 3:
        return -x - y;
    }
  }

  /**
   * 2D Perlin noise function.
   * Returns a value in the range [-1, 1].
   * @param {number} x
   * @param {number} y
   * @param {Uint8Array} perm - The permutation table.
   */
  static noise2D(x, y, perm) {
    const X = Math.floor(x) & 255;
    const Y = Math.floor(y) & 255;

    const xf = x - Math.floor(x);
    const yf = y - Math.floor(y);

    const topRight = perm[perm[X + 1] + Y + 1];
    const topLeft = perm[perm[X] + Y + 1];
    const bottomRight = perm[perm[X + 1] + Y];
    const bottomLeft = perm[perm[X] + Y];

    const u = this.fade(xf);
    const v = this.fade(yf);

    const valTopRight = this.grad(topRight, xf - 1, yf - 1);
    const valTopLeft = this.grad(topLeft, xf, yf - 1);
    const valBottomRight = this.grad(bottomRight, xf - 1, yf);
    const valBottomLeft = this.grad(bottomLeft, xf, yf);

    const lerpTop = this.lerp(valTopLeft, valTopRight, u);
    const lerpBottom = this.lerp(valBottomLeft, valBottomRight, u);
    return this.lerp(lerpBottom, lerpTop, v);
  }
}

export default IslandGenerator;
